import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Button,
  Avatar
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import { useExpense } from '../../context/ExpenseContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import AddExpenseDialog from '../AddExpenseDialog/AddExpenseDialog';
import './ExpenseList.scss';

const ExpenseList = () => {
  const { getFilteredExpenses, deleteExpense, getTotalExpenses } = useExpense();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  
  const expenses = getFilteredExpenses();
  const totalExpenses = getTotalExpenses();

  const handleMenuOpen = (event, expense) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedExpense(expense);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedExpense(null);
  };

  const handleDeleteExpense = () => {
    if (selectedExpense && window.confirm(`Are you sure you want to delete this expense?`)) {
      deleteExpense(selectedExpense.id);
    }
    handleMenuClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'General': '#757575',
      'Food & Drinks': '#ff9800',
      'Transportation': '#2196f3',
      'Entertainment': '#9c27b0',
      'Equipment': '#4caf50',
      'Tips': '#f44336',
      'House': '#8bc34a',
      'Other': '#795548'
    };
    return colors[category] || '#757575';
  };

  if (expenses.length === 0) {
    return (
      <Card className="expense-list empty">
        <CardContent>
          <Box className="empty-state">
            <ReceiptIcon className="empty-icon" />
            <Typography variant="h6" className="empty-title">
              No Expenses Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" className="empty-subtitle">
              Add miscellaneous expenses to keep track of your spending
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<AddIcon />}
              onClick={() => setAddExpenseOpen(true)}
              className="add-expense-button"
            >
              Add First Expense
            </Button>
          </Box>
        </CardContent>
        
        <AddExpenseDialog
          open={addExpenseOpen}
          onClose={() => setAddExpenseOpen(false)}
        />
      </Card>
    );
  }

  const sortedExpenses = [...expenses].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );

  const linkedExpenses = sortedExpenses.filter(expense => expense.playerId);
  const unlinkedExpenses = sortedExpenses.filter(expense => !expense.playerId);

  return (
    <>
      <Card className="expense-list">
        <CardContent>
          <Box className="expense-header">
            <Box className="header-info">
              <ReceiptIcon className="expense-icon" />
              <Box>
                <Typography variant="h6">
                  Miscellaneous Expenses ({expenses.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total: {formatCurrency(totalExpenses)} | 
                  Linked: {linkedExpenses.length} | 
                  General: {unlinkedExpenses.length}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddExpenseOpen(true)}
              className="add-expense-btn"
            >
              Add Expense
            </Button>
          </Box>
          
          <Divider className="header-divider" />
          
          <Box className="expense-items">
            {sortedExpenses.map((expense, index) => {
              const playerInitials = expense.playerName ? getInitials(expense.playerName) : null;
              
              return (
                <Box key={expense.id} className="expense-item">
                  <Box className="item-header">
                    <Box className="expense-info">
                      <Chip 
                        label={formatCurrency(expense.amount)}
                        color="error"
                        className="amount-chip"
                      />
                      <Chip 
                        label={expense.category}
                        size="small"
                        className="category-chip"
                        style={{
                          backgroundColor: getCategoryColor(expense.category),
                          color: 'white'
                        }}
                      />
                      {expense.playerId && expense.playerName && (
                        <Chip 
                          avatar={
                            <Avatar 
                              sx={{
                                width: 20,
                                height: 20,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                bgcolor: '#4caf50',
                                color: 'white'
                              }}
                            >
                              {playerInitials}
                            </Avatar>
                          }
                          label={expense.playerName}
                          size="small"
                          className="player-chip"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {!expense.playerId && (
                        <Chip 
                          icon={<PersonIcon />}
                          label="General"
                          size="small"
                          className="general-chip"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Box className="item-actions">
                      <Typography variant="body2" color="text.secondary" className="date">
                        {formatDate(expense.timestamp)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, expense)}
                        className="menu-button"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Typography variant="body1" className="description">
                    {expense.description}
                  </Typography>
                  
                  {expense.playerId && expense.playerName && (
                    <Box className="player-info">
                      <Typography variant="body2" color="text.secondary">
                        Linked to {expense.playerName}
                      </Typography>
                    </Box>
                  )}
                  
                  {index < sortedExpenses.length - 1 && (
                    <Divider className="item-divider" />
                  )}
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDeleteExpense} sx={{ color: 'error.main' }}>
          Delete Expense
        </MenuItem>
      </Menu>

      <AddExpenseDialog
        open={addExpenseOpen}
        onClose={() => setAddExpenseOpen(false)}
      />
    </>
  );
};

export default ExpenseList;